import React, { useState, useEffect, useRef } from 'react';
import { fetchVenues, fetchVenueTotals } from '../api/client';
import { useAuth } from '../context/AuthContext';
import RankingTable from '../components/RankingTable';
import Poster from '../components/Poster';
import Podium from '../components/Podium';
import { MoreButton } from '../components/VenueCharts';
import { israelToday, relevantWeekend, shiftStr, fmtShortRange } from '../components/DateRangePicker';
import {
  PAGE_MAX, fmt, card, ltr, Chip, ChipRow, Head, Stepper, PhoneRow, CustomRange, Change, Occ, Share, sharePct,
  PlaceTile, fairChange,
} from '../components/RankingParts';
import useWindowWidth from '../hooks/useWindowWidth';

/**
 * סניפים (§12.13) — built like דירוגים: four period cards (this year, this
 * month, the weekend, today — the weekend by default), each with its total
 * and leading chain; chips for chains / all branches / cities; then one
 * ranking with its own total, a podium and the table from #1. A chain or a
 * city opens its branches in place ("כל הרשתות" / "כל הערים" goes back); a
 * branch opens its own page. The change column shows only where the
 * comparison is fair (`fairChange`). `view` lives in App, so a branch and
 * back lands on the same period, chips and drill-down. Layout rules: §0.
 */

const HEB_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (s) => s.split('-').map(Number);
const ALL_TIME = { start: '2000-01-01', end: '2099-12-31' };

export const defaultVenueView = () => ({
  period: 'weekend', anchor: relevantWeekend().start, custom: null, tab: 'chains', chainId: null, city: null,
});

/* ── periods ───────────────────────────────────────────────────────────── */
export function venueRange(v) {
  const [y, m] = ymd(v.anchor);
  if (v.period === 'year') return { start: `${y}-01-01`, end: `${y}-12-31` };
  if (v.period === 'month') return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(new Date(y, m, 0).getDate())}` };
  if (v.period === 'weekend') return { start: v.anchor, end: shiftStr(v.anchor, 3) };
  if (v.period === 'day') return { start: v.anchor, end: v.anchor };
  if (v.period === 'custom' && v.custom) return v.custom;
  return ALL_TIME;
}
// The branch page's own picker speaks in modes; a period maps onto one and back.
export const venueRangeWithMode = (v) => ({
  ...venueRange(v), mode: v.period === 'day' ? 'today' : v.period === 'weekend' ? 'weekend' : 'custom',
});
export function venueViewFromRange(v, r) {
  if (r.mode === 'today') return { ...v, period: 'day', anchor: r.start, custom: null };
  if (r.mode === 'weekend') return { ...v, period: 'weekend', anchor: r.start, custom: null };
  return { ...v, period: 'custom', custom: { start: r.start, end: r.end } };
}
function isCurrent(v, today) {
  if (v.period === 'weekend') return v.anchor === relevantWeekend().start;
  if (!['year', 'month', 'day'].includes(v.period)) return false;
  const r = venueRange(v);
  return r.start <= today && today <= r.end;
}
function stepAnchor(v, dir) {
  const [y, m] = ymd(v.anchor);
  if (v.period === 'year') return `${y + dir}-01-01`;
  if (v.period === 'month') { const d = new Date(y, m - 1 + dir, 1); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; }
  return shiftStr(v.anchor, dir * (v.period === 'weekend' ? 7 : 1));
}
function periodLabel(v, today) {
  const [y, m, d] = ymd(v.anchor);
  if (v.period === 'year') return String(y);
  if (v.period === 'month') return `${HEB_MONTHS[m - 1]} ${y}`;
  if (v.period === 'weekend') return ltr(fmtShortRange(v.anchor, shiftStr(v.anchor, 3)) + (y !== ymd(today)[0] ? `.${y}` : ''));
  if (v.period === 'day') return v.anchor === today ? <>היום · {ltr(`${d}.${m}`)}</> : ltr(`${d}.${m}.${y}`);
  if (v.period === 'custom' && v.custom) return ltr(fmtShortRange(v.custom.start, v.custom.end));
  return 'כל הזמנים';
}

/* ── the four totals ───────────────────────────────────────────────────── */
function PeriodCards({ totals, view, today, mob, onPick, onAll, onCustom }) {
  const [, m, d] = ymd(today);
  const wk = relevantWeekend();
  const cards = [
    { key: 'year', label: today.slice(0, 4) },
    { key: 'month', label: HEB_MONTHS[m - 1] },
    { key: 'weekend', label: <>סופ״ש · {ltr(fmtShortRange(wk.start, wk.end))}</> },
    { key: 'day', label: <>היום · {ltr(`${d}.${m}`)}</> },
  ];
  const quiet = (on) => ({ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
                            fontSize: 12.5, textDecoration: 'underline', color: on ? 'var(--accent)' : 'var(--faint)', fontWeight: on ? 700 : 400 });
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : 'repeat(4, minmax(0, 1fr))', gap: mob ? 10 : 16 }}>
        {cards.map((p) => {
          const t = totals?.[p.key];
          const on = view.period === p.key && isCurrent(view, today);
          const lead = t?.leader;
          return (
            <div key={p.key} data-period-card={p.key} onClick={() => onPick(p.key)} style={{
              ...card, cursor: 'pointer', padding: mob ? '14px 16px' : '22px 24px',
              border: on ? '2px solid var(--accent)' : '2px solid transparent',
              display: mob ? 'flex' : 'block', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: mob ? 14 : 15, fontWeight: 800, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{p.label}</div>
                {!mob && <div data-total style={{ fontSize: 34, fontWeight: 900, color: 'var(--accent)', letterSpacing: -1.5, lineHeight: 1.1, margin: '8px 0 12px' }}>{t ? fmt(t.tickets) : '…'}</div>}
                {lead && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: mob ? 4 : 0, minWidth: 0 }}>
                    <PlaceTile kind="chain" />
                    <span style={{ fontSize: 12, color: 'var(--faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      מובילה: <b style={{ color: 'var(--txt)', fontWeight: 700 }}>{lead.name_he}</b>
                    </span>
                  </div>
                )}
              </div>
              {mob && <div data-total style={{ fontSize: 26, fontWeight: 900, color: 'var(--accent)', letterSpacing: -1, flexShrink: 0 }}>{t ? fmt(t.tickets) : '…'}</div>}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <button data-link="all" style={quiet(view.period === 'all')} onClick={onAll}>כל הזמנים</button>
        <button data-link="custom" style={quiet(view.period === 'custom')} onClick={onCustom}>טווח מותאם</button>
      </div>
    </div>
  );
}

/* ── the ranking ───────────────────────────────────────────────────────── */
const filmName = (m) => m.title_he || m.title;
const TopFilm = ({ top }) => (top ? (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <Poster posterUrl={top.poster_url} genre={top.genre} letter={(filmName(top) || '?')[0]} size="sm" />
    <span style={{ fontSize: 12.5, color: 'var(--txt)' }}>{filmName(top)}</span>
  </div>
) : <span style={{ color: 'var(--faint)' }}>—</span>);

// Which list, under which title, with which line under each name.
function listOf(view, data) {
  const live = (rows) => rows.filter((r) => r.tickets > 0 || r.screenings > 0);
  if (view.chainId) {
    const chain = data.chains.find((c) => c.chain_id === view.chainId);
    const strip = (n) => (chain && n.startsWith(`${chain.name_he} `) ? n.slice(chain.name_he.length + 1) : n);
    return {
      kind: 'branch', title: chain ? chain.name_he : 'רשת', back: 'כל הרשתות',
      rows: live(data.cinemas.filter((c) => c.chain_id === view.chainId)).map((r) => ({ ...r, label: strip(r.name_he), share: r.chain_share })),
      // "גלילות" needs no "גלילות" under it: the city only where the name is not it.
      sub: (r) => (r.city_he && r.city_he !== r.label ? r.city_he : ''), shareHead: 'נתח ברשת', count: 'סניפים',
    };
  }
  if (view.city) {
    const rows = live(data.cinemas.filter((c) => c.city === view.city));
    const sum = rows.reduce((s, r) => s + r.tickets, 0);
    const city = data.cities.find((c) => c.city === view.city);
    return {
      kind: 'branch', title: city ? city.name_he : view.city, back: 'כל הערים',
      rows: rows.map((r) => ({ ...r, label: r.name_he, share: sum ? Math.round(r.tickets * 100 / sum) : 0 })),
      sub: (r) => r.chain_he, shareHead: 'נתח בעיר', count: 'סניפים',
    };
  }
  if (view.tab === 'cinemas') {
    return {
      kind: 'branch', title: 'כל הסניפים',
      rows: live(data.cinemas).map((r) => ({ ...r, label: r.name_he, share: r.market_share })),
      sub: (r) => `${r.chain_he} · ${r.city_he}`, shareHead: 'נתח', count: 'סניפים',
    };
  }
  if (view.tab === 'cities') {
    return {
      kind: 'city', title: 'ערים',
      rows: live(data.cities).map((r) => ({ ...r, label: r.name_he, share: r.market_share })),
      sub: (r) => r.chains.join(' · '), shareHead: 'נתח', count: 'ערים', branches: true,
    };
  }
  return {
    kind: 'chain', title: 'רשתות',
    rows: live(data.chains).map((r) => ({ ...r, label: r.name_he, share: r.market_share })),
    sub: (r) => `${r.cinemas} סניפים`, shareHead: 'נתח', count: 'רשתות', branches: true,
  };
}

function Ranking({ view, data, loading, today, mob, onStep, onRow }) {
  const [shown, setShown] = useState(20);
  useEffect(() => { setShown(20); }, [view.tab, view.chainId, view.city, view.period, view.anchor]);
  if (!data) return <div style={{ ...card, textAlign: 'center', color: 'var(--faint)', padding: 40 }}>{loading ? 'טוען...' : 'טעינת הנתונים נכשלה'}</div>;

  const L = listOf(view, data);
  const rows = [...L.rows].sort((a, b) => b.tickets - a.tickets);
  const drilled = !!(view.chainId || view.city);
  const total = drilled ? rows.reduce((s, r) => s + r.tickets, 0) : data.totals.tickets;
  const prev = drilled ? rows.reduce((s, r) => s + (r.prev_tickets || 0), 0) : data.totals.prev_tickets;
  const screenings = drilled ? rows.reduce((s, r) => s + r.screenings, 0) : data.totals.screenings;
  const withChange = fairChange(view.period, venueRange(view), today);
  const branchesHere = L.kind === 'branch' ? rows.length : data.totals.active_cinemas;
  const line = L.kind === 'branch'
    ? `${fmt(rows.length)} סניפים · ${fmt(screenings)} הקרנות`
    : `${fmt(rows.length)} ${L.count} · ${fmt(branchesHere)} סניפים`;
  const steppable = ['year', 'month', 'weekend', 'day'].includes(view.period);
  const atNow = isCurrent(view, today);
  const tile = (size) => <PlaceTile kind={L.kind === 'chain' ? 'chain' : 'branch'} size={size} />;

  const cols = [
    { key: 'label', header: L.kind === 'chain' ? 'רשת' : L.kind === 'city' ? 'עיר' : 'סניף', align: 'right', sortValue: (r) => r.label, render: (r) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        {tile('sm')}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--txt)' }}>{r.label}</div>
          {L.sub(r) && <div style={{ fontSize: 11, color: 'var(--faint)' }}>{L.sub(r)}</div>}
        </div>
      </div>
    ) },
    { key: 'tickets', header: 'כרטיסים', align: 'center', render: (r) => <b style={{ color: 'var(--accent)' }}>{fmt(r.tickets)}</b> },
    ...(withChange ? [{ key: 'change', header: 'שינוי', align: 'center',
                        sortValue: (r) => (r.prev_tickets ? (r.tickets - r.prev_tickets) / r.prev_tickets : Infinity),
                        render: (r) => <Change curr={r.tickets} prev={r.prev_tickets} /> }] : []),
    { key: 'share', header: L.shareHead, align: 'center', render: (r) => <Share share={r.share} tickets={r.tickets} /> },
    { key: 'occupancy', header: 'תפוסה', align: 'center', render: (r) => <Occ v={r.occupancy} /> },
    { key: 'screenings', header: 'הקרנות', align: 'center', render: (r) => <span style={{ color: 'var(--muted)' }}>{fmt(r.screenings)}</span> },
    ...(L.branches ? [{ key: 'cinemas', header: 'סניפים', align: 'center', render: (r) => <span style={{ color: 'var(--muted)' }}>{r.cinemas}</span> }] : []),
    { key: 'top', header: 'סרט מוביל', align: 'right', sortValue: (r) => (r.top ? filmName(r.top) : ''), render: (r) => <TopFilm top={r.top} /> },
  ];

  return (
    <div data-ranking style={{ ...card, padding: mob ? 16 : 22, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
      <Head mob={mob} title={L.title} total={total}
        nav={<Stepper label={periodLabel(view, today)} onPrev={steppable ? () => onStep(-1) : null}
                      onNext={steppable ? () => onStep(1) : null} nextDisabled={atNow} />}
        line={<>{withChange && prev > 0 && <Change curr={total} prev={prev} />}<span>{line}</span></>} />
      {rows.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--faint)', padding: 30, fontSize: 13 }}>אין נתונים לתקופה זו</div>
      ) : (
        <>
          <Podium items={rows} mob={mob} max={5} itemKey={(r) => r.cinema_id || r.chain_id || r.city}
            onClick={onRow} poster={() => tile(mob ? 'md' : 'xl')} name={(r) => r.label} value={(r) => r.tickets}
            extra={(r) => L.sub(r) && <span style={{ fontSize: 11, color: 'var(--faint)' }}>{L.sub(r)}</span>} />
          {mob ? (
            <div data-phone-list>
              {rows.slice(0, shown).map((r, i) => (
                <PhoneRow key={r.cinema_id || r.chain_id || r.city} i={i} poster={tile('sm')} name={r.label}
                  sub={[L.sub(r), r.top && `מוביל: ${filmName(r.top)}`].filter(Boolean).join(' · ')} tickets={r.tickets}
                  line1={<>{withChange && <><Change curr={r.tickets} prev={r.prev_tickets} size={10} /> · </>}נתח {ltr(sharePct(r.share, r.tickets))} · <Occ v={r.occupancy} /></>}
                  line2={`${fmt(r.screenings)} הקרנות`} onClick={() => onRow(r)} />
              ))}
              {rows.length > shown && <MoreButton onClick={() => setShown((n) => n + 20)}>הצג עוד</MoreButton>}
            </div>
          ) : (
            <RankingTable columns={cols} data={rows} rankColumn pageSize={20} minWidth={760}
                          initialSort={{ key: 'tickets', dir: 'desc' }} onRowClick={onRow} />
          )}
        </>
      )}
    </div>
  );
}

/* ── the page ──────────────────────────────────────────────────────────── */
function CinemasPage({ refreshNonce, view, onViewChange, onCinemaClick }) {
  const { hasPermission } = useAuth();
  const [own, setOwn] = useState(defaultVenueView);
  const v = view || own;
  const setView = onViewChange || setOwn;
  const set = (patch) => setView({ ...v, ...patch });
  const today = israelToday();
  const mob = useWindowWidth() < 768;
  const range = venueRange(v);

  const [totals, setTotals] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  useEffect(() => {
    if (!hasPermission('cinemas.kpi')) return;
    fetchVenueTotals().then(setTotals).catch(() => setTotals(null));
  }, [refreshNonce]); // eslint-disable-line

  useEffect(() => {
    const my = ++seq.current;
    setLoading(true);
    fetchVenues(range.start, range.end)
      .then((d) => { if (my === seq.current) { setData(d); setLoading(false); } })
      .catch(() => { if (my === seq.current) { setData(null); setLoading(false); } });
  }, [range.start, range.end, refreshNonce]);

  const tabs = [
    { key: 'chains', label: 'רשתות' },
    { key: 'cinemas', label: 'כל הסניפים' },
    ...(hasPermission('cinemas.cities') ? [{ key: 'cities', label: 'ערים' }] : []),
  ];
  const tab = tabs.some((t) => t.key === v.tab) ? v.tab : 'chains';
  const chain = data && v.chainId ? data.chains.find((c) => c.chain_id === v.chainId) : null;
  const city = data && v.city ? data.cities.find((c) => c.city === v.city) : null;

  const pick = (period) => setView({ ...v, period, anchor: period === 'weekend' ? relevantWeekend().start : today, custom: null });
  const onRow = (r) => {
    if (r.cinema_id) onCinemaClick(r.cinema_id);
    else if (r.chain_id) set({ chainId: r.chain_id, city: null, tab: 'chains' });
    else if (r.city) set({ city: r.city, chainId: null, tab: 'cities' });
  };

  return (
    <div style={{ maxWidth: PAGE_MAX, margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {hasPermission('cinemas.kpi') && (
        <PeriodCards totals={totals} view={v} today={today} mob={mob} onPick={pick}
          onAll={() => setView({ ...v, period: 'all', custom: null })}
          onCustom={() => setView({ ...v, period: 'custom', custom: v.custom || { start: `${today.slice(0, 8)}01`, end: today } })} />
      )}
      {v.period === 'custom' && v.custom && (
        <CustomRange value={v.custom} onChange={(c) => setView({ ...v, custom: c })} />
      )}
      {hasPermission('cinemas.table') && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ChipRow>
              {tabs.map((t) => <Chip key={t.key} label={t.label} active={tab === t.key}
                                     onClick={() => set({ tab: t.key, chainId: null, city: null })} />)}
            </ChipRow>
            {(v.chainId || v.city) && (
              <ChipRow small>
                <Chip small label={v.chainId ? 'כל הרשתות' : 'כל הערים'} onClick={() => set({ chainId: null, city: null })} />
                <Chip small active label={v.chainId ? (chain ? chain.name_he : '…') : (city ? city.name_he : v.city)} />
              </ChipRow>
            )}
          </div>
          <Ranking view={{ ...v, tab }} data={data} loading={loading} today={today} mob={mob}
                   onStep={(dir) => set({ anchor: stepAnchor(v, dir) })} onRow={onRow} />
        </>
      )}
    </div>
  );
}

export default CinemasPage;
